// FirstRunGateway — the zero-engagements floor (027-engagement-aggregate, Slice
// C, FR-010, User Story 1, SC-003). When the practitioner has no engagement,
// App.tsx renders ONLY this centred "Create your first engagement" panel instead
// of an empty/lying dashboard. The two-field form captures the client and a
// clear title, then creates a draft engagement which becomes
// current and unmounts the gateway.
//
// CORAL: this is the ONE place the reserved coral --ap-accent CTA is allowed —
// the primary button carries data-cta="first-run" so the colour spend is provably
// gateway-scoped (the dropdown / modal CTAs stay neutral/blue).

import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import '../styles/FirstRunGateway.css';

interface Props {
  onCreate: (input: { client: string; title: string; commandId: string }) => unknown;
}

function newCommandId(): string {
  try {
    return `create-${crypto.randomUUID()}`;
  } catch {
    return `create-${Date.now().toString(36)}`;
  }
}

const FirstRunGateway: React.FC<Props> = ({ onCreate }) => {
  const [client, setClient] = useState('');
  const [title, setTitle] = useState('');
  const [commandId] = useState(newCommandId);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    const trimmed = client.trim();
    const trimmedTitle = title.trim();
    if (!trimmed || !trimmedTitle) {
      setError('Enter a client organisation and engagement title.');
      return;
    }
    setError(null);
    setCreating(true);
    try {
      const created = await onCreate({ client: trimmed, title: trimmedTitle, commandId });
      if (!created) {
        setError("We couldn't create the engagement. Your entries have not been lost. Try again.");
      }
    } catch {
      setError("We couldn't create the engagement. Your entries have not been lost. Try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="first-run-gateway">
      <div className="first-run-panel">
        <div className="first-run-icon" aria-hidden="true">
          <Building2 size={26} />
        </div>
        <h1 className="first-run-title">Create your first engagement</h1>
        <p className="first-run-lead">
          Enter the client and engagement name. You will add the brief, people and activities next.
        </p>

        <form className="first-run-form" onSubmit={handleSubmit}>
          <label className="first-run-label" htmlFor="first-run-client">
            Client organisation *
          </label>
          <input
            id="first-run-client"
            className="first-run-input"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="e.g. Northwind Mutual"
            required
            autoFocus
            aria-label="Client"
          />

          <label className="first-run-label" htmlFor="first-run-title">
            Engagement title *
          </label>
          <input
            id="first-run-title"
            className="first-run-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Claims service modernisation"
            aria-label="Engagement title"
            required
          />
          <p className="first-run-hint">Use a name the client will recognise.</p>

          {error && (
            <div className="first-run-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="first-run-cta"
            data-cta="first-run"
            disabled={creating}
            aria-label={creating ? 'Creating engagement' : 'Create your first engagement and continue setup'}
          >
            {creating ? 'Creating engagement...' : 'Continue setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FirstRunGateway;
